import { Box, Flex, Image, Text } from "@chakra-ui/react"
import { forwardRef, useMemo } from "react"
import { useIsMobileDevice } from "src/util/browser"
import { InspirationModel } from "./Inspirations"
import useGridPageStyling from "../hooks/useGridPageStyling"

type Props = {
  inspiration?: InspirationModel
  showAvatar?: boolean
  onClick?: (inspiration: InspirationModel) => void
  miniCard?: boolean
  isTransparent?: boolean
}

export const Inspiration = forwardRef<HTMLDivElement, Props>(
  (
    {
      inspiration,
      showAvatar = false,
      onClick,
      miniCard = false,
      isTransparent = false
    }: Props,
    ref
  ) => {
    const branding = useGridPageStyling()
    const isMobile = useIsMobileDevice()

    const cardStyle = useMemo(() => {
      if (miniCard) {
        return {
          cardHeight: "unset",
          cardWidth: isMobile ? "86%" : "280px",
          thumbnailWidth: "50px",
          thumbnailHeight: "50px",
          avatarHeight: "30px",
          avatarWidth: "30px",
          descriptionNoOfLine: 2
        }
      }
      return {
        cardHeight: "unset",
        cardWidth: "100%",
        thumbnailWidth: "50px",
        thumbnailHeight: "50px",
        avatarHeight: "32px",
        avatarWidth: "32px",
        descriptionNoOfLine: 4
      }
    }, [miniCard])

    return (
      <Box
        ref={ref}
        onClick={() => onClick(inspiration)}
        minWidth={[cardStyle.cardWidth, "280px", "280px"]}
        maxWidth={cardStyle.avatarWidth}
        height={cardStyle.cardHeight}
        minHeight={cardStyle.cardHeight}
        p={3}
        bg={
          isTransparent ? "rgba(0, 0, 0, 0.5)" : branding.panelBackgroundColor
        }
        color={branding.panelTextColor}
        borderColor={branding.panelBorderColor}
        borderWidth={isTransparent ? 0 : 1}
        _hover={
          !isMobile && {
            bg: branding.panelMouseoverBackgroundColor,
            color: branding.panelMouseoverTextColor,
            borderColor: branding.panelMouseoverBorderColor
          }
        }
        borderRadius={"6px"}
        shadow="md"
        style={{ scrollSnapAlign: "center" }}
        cursor="pointer"
        role="group" // added group for applying _groupHover to child
      >
        <Flex height={"full"} width={"full"} direction={"row"}>
          <Box pr={1} position={"relative"} display="flex" alignItems="center">
            <Image
              borderRadius={"6px"}
              objectFit="cover"
              minWidth={cardStyle.thumbnailWidth}
              width={cardStyle.thumbnailWidth}
              height={cardStyle.thumbnailHeight}
              src={inspiration?.thumbnailURL}
            />
            {showAvatar && inspiration.avatar?.bubbleImage && (
              <Box
                rounded="5px"
                overflow={"clip"}
                width={cardStyle.avatarWidth}
                height={cardStyle.avatarHeight}
                bottom={-2}
                right={0}
                position={"absolute"}
                border="2px solid"
                borderColor={branding.panelBackgroundColor}
                _groupHover={
                  !isMobile && {
                    borderColor: branding.panelMouseoverBackgroundColor
                  }
                }
                bg="black"
              >
                <Image
                  objectFit={"cover"}
                  height="100%"
                  width="100%"
                  overflow="hidden"
                  src={inspiration.avatar?.bubbleImage}
                />
              </Box>
            )}
          </Box>
          <Flex height={"full"} px={1} direction={"column"} overflow="hidden">
            <Text
              fontWeight={"semibold"}
              whiteSpace="nowrap"
              textOverflow={"ellipsis"}
              overflow={"hidden"}
              fontSize={"18px"}
            >
              {inspiration.title}
            </Text>
            <Text
              whiteSpace="normal"
              noOfLines={cardStyle.descriptionNoOfLine}
              fontSize={"12px"}
            >
              {inspiration.description}
            </Text>
          </Flex>
        </Flex>
      </Box>
    )
  }
)
